import axios from "../../api/axios";
import { encryptToken } from "../../utils/tokenUtils";

// Base URL from environment variable
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AuthService = {
  isAuthenticated: () => {
    return !!localStorage.getItem("authToken");
  },

  // status = 1 => success -> OTP sent -> OTP verified -> Dashboard
  // status = 2 => OTP sent -> OTP verified -> error -> if refresh -> login
  login: async (identity, password) => {
    try {
      const response = await axios.post(`${BASE_URL}/login`, { identity, password });
      const { message, success, token, device, user } = response.data;

      if (success && token) {
        // Handle successful token-based login
        const encryptedToken = encryptToken(token);
        localStorage.setItem("authToken", `encrypted:${encryptedToken}`);
        localStorage.setItem("authenticated", true);

        return { success: true, token, message };
      }

      if (success && device) {
        // Handle OTP flow
        return { success: true, message, device, user };
      }

      return { success: false, message };
    } catch (error) {
      const errorMessage = error.response?.data.message || "Login failed";
      return { success: false, message: errorMessage };
    }
  },

  async magicLogin(token) {
    try {
      const response = await axios.post('/auth/magic/verify', { token });
      if (response.data.success) {
        const authToken = response.data.token;
        // Encrypt and store the token
        const encryptedToken = encryptToken(authToken);
        localStorage.setItem("authToken", `encrypted:${encryptedToken}`);
        localStorage.setItem("authenticated", true);

        return {
          success: true,
          token: authToken,
          message: response.data.message,
        };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      console.error("Magic login failed", error);
      const errorMessage = error.response?.data?.message || error.message || "Magic link authentication failed";
      return { success: false, message: errorMessage };
    }
  },

  loginWithGoogle: async (token) => {
    try {
      const response = await axios.post(`${BASE_URL}/auth/google/login`, {
        token
      });

      const { message, success, token: authToken, user, device } = response.data;

      if (success && authToken) {
        // Only handle token encryption and storage here
        const encryptedToken = encryptToken(authToken);
        localStorage.setItem("authToken", `encrypted:${encryptedToken}`);
        localStorage.setItem("authenticated", true);

        // Pass the device to the caller for handling
        return { success: true, token: authToken, message, user, device };
      }

      // Unsuccessful but has device info
      if (success && device) {
        return { success: true, message, device, user };
      }

      return { success: false, message };
    } catch (error) {
      console.error("Google login failed", error);
      const errorMessage = error.response?.data.message || "Google login failed";
      return { success: false, message: errorMessage };
    }
  },

  loginOtp: async (email, otp) => {
    try {
      const response = await axios.post(`${BASE_URL}/loginOtp`, { email, otp });
      const encryptedToken = encryptToken(response.data.token);
      localStorage.setItem("authToken", `encrypted:${encryptedToken}`);
      localStorage.setItem("authenticated", true);
      return { success: true, token: response.data.token };
    } catch (error) {
      console.error("OTP login failed", error);
      const errorMessage = error.response?.data?.message || "OTP login failed";
      return { success: false, error: errorMessage };
    }
  },

  register: async (userData) => {
    try {
      const response = await axios.post("/register", userData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Registration failed", error);
      const errorMessage = error.response?.data?.message || error.response?.data || "Registration failed";
      return { success: false, error: errorMessage };
    }
  },
  initialRegister: async (userData) => {
    try {
      const response = await axios.post(`${BASE_URL}/register/info`, userData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Initial registration failed", error);
      const errorMessage = error.response?.data?.message || error.response?.data || "Initial registration failed";
      return { success: false, error: errorMessage };
    }
  },

  setEducationalLevel: async (educationalData) => {
    try {
      const response = await axios.post(`${BASE_URL}/register/edu`, educationalData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Setting educational level failed", error);
      const errorMessage = error.response?.data?.message || error.response?.data || "Setting educational level failed";
      return { success: false, error: errorMessage };
    }
  },


  getUser: async () => {
    try {
      const response = await axios.get("/account");
      // console.log("response", response.data);
      const { user } = response.data; // Destructure user from response
      return { success: true, user };
    } catch (error) {
      console.error("Failed to fetch user data", error);
      return { success: false, error: error.message || 'Failed to fetch user data' };
    }
  },
  verifyEmail: async (token) => {
    try {
      const response = await axios.get(`/verify-email/${token}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error("Email verification failed", error);
      return { success: false, error };
    }
  },
  checkPassword: async (password) => {
    try {
      const response = await axios.post('users/check_password', {
        password: password
      });
      // console.log("response from service", response.data.isPasswordCorrect)
      return response.data.isPasswordCorrect;

    } catch (error) {
      console.error("Password check failed", error);
      return false;
    }
  },

  getSecuritySettings: async () => {
    try {
      const response = await axios.get('users/security_settings');
      // console.log("response from service", response)
      return response.data;
    } catch (error) {
      console.error("Failed to get security settings", error);
      return null;
    }
  },

  getBankDetails: async () => {
    try {
      const response = await axios.get('users/bank_details');
      return { success: true, data: response.data.bankDetails };
    } catch (error) {
      console.error('Error fetching bank details', error);
      return { success: false, error };
    }
  },

  updateSecurityLayers: async (newPassword, isEmailOTP, isSmsOTP, isPinOTP, pinValue) => {
    try {
      const response = await axios.post('users/update_security_layers', {
        new_password: newPassword,
        isEmailOTP: isEmailOTP,
        isSmsOTP: isSmsOTP,
        isPinOTP: isPinOTP,
        pin: pinValue
      });
      return response.data;
    } catch (error) {
      console.error("Failed to update security layers", error);
      return null;
    }
  },

  logout: async () => {
    try {
      const response = await axios.post('/logout');
      // console.log(response.data.message); 

      // Clear local storage after successful logout
      localStorage.removeItem("authToken");
      localStorage.removeItem("authenticated");

      return { success: true, message: response.data.message };
    } catch (error) {
      console.error("Logout failed", error);
      return { success: false, error: error.response?.data?.message || error.message || 'An error occurred during logout' };
    }
  },

  resendOtp: async (email) => {
    try {
      const response = await axios.post(`${BASE_URL}/resend-otp`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error("Resend OTP failed", error);
      return { success: false, error: error.response?.data?.message || 'Resend OTP failed' };
    }
  },
  resetPasswordRequest: async (email) => {
    try {
      const response = await axios.post(`${BASE_URL}/reset-password-request`, { email });
      return { success: true, ...response.data };
    } catch (error) {
      console.error("Password reset request failed", error);
      return { success: false, error: error.response?.data || 'Password reset request failed' };
    }
  },
  resetPasswordSubmit: async (token, password, confirmPassword) => {
    try {
      const response = await axios.post(`${BASE_URL}/reset-password-submit`, { token, password, confirm_password: confirmPassword });
      return { success: true, ...response.data };
    } catch (error) {
      console.error("Password reset submission failed", error);
      return { success: false, error: error.response?.data || 'Password reset submission failed' };
    }
  },
  validateResetToken: async (token) => {
    // Dummy implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true, message: "Token is valid" });
      }, 1000); // Simulate network delay
    });
  },

  // validateResetToken: async (token) => {
  //   try {
  //     const response = await axios.post(`${BASE_URL}/validate-reset-token`, { token });
  //     return response.data;
  //   } catch (error) {
  //     console.error("Token validation failed", error);
  //     return { success: false, error: error.response?.data || 'Token validation failed' };
  //   }
  // },
};

export default AuthService;
